import {getDocumentUrl, IUserData, IUserPresenceData, Organization} from "@buildwithflux/core";
import {
    Enterprise,
    EnterpriseOrganization,
    EnterpriseWithDefaultOrganization,
    IBaseDocumentData,
    Slug,
} from "@buildwithflux/models";
import {areWeTestingWithJest, isDevEnv} from "@buildwithflux/shared";
import {StoreApi, UseBoundStore, create} from "zustand";
import {devtools} from "zustand/middleware";
import {useShallow} from "zustand/react/shallow";

import {useFluxServices} from "injection/hooks";

/**
 * State for managing document metadata
 *
 * This replaces the Redux documentMeta reducer state, which tracked document owner,
 * organization owner, enterprise owner, remote active users, and base document data.
 */
type DocumentMetaStoreState = {
    /**
     * The owner of the current document (user data)
     */
    documentOwner?: IUserData;

    /**
     * The organization that owns the current document
     */
    documentOrganizationOwner?: Organization;

    /**
     * The enterprise that owns the current document
     */
    documentEnterpriseOwner?: Enterprise;

    /**
     * The default organization for the enterprise owner
     */
    documentEnterpriseOwnerDefaultOrganization?: EnterpriseOrganization;

    /**
     * Array of remote users currently active in the document
     */
    remoteActiveUsers: IUserPresenceData[];

    /**
     * Host document for local component (when viewing a local component)
     */
    hostDocumentForLocalComponent?: IBaseDocumentData;

    /**
     * Base document data for the current document
     */
    baseDocument?: IBaseDocumentData;

    /**
     * Sets the document owner
     */
    setDocumentOwner: (owner?: IUserData) => void;

    /**
     * Sets the document organization owner
     */
    setDocumentOrganizationOwner: (organizationOwner?: Organization) => void;

    /**
     * Sets the document enterprise owner
     */
    setDocumentEnterpriseOwner: (enterpriseOwner?: Enterprise) => void;

    /**
     * Sets the default organization for the enterprise owner
     */
    setDocumentEnterpriseOwnerDefaultOrganization: (
        enterpriseOwnerDefaultOrganization?: EnterpriseOrganization,
    ) => void;

    /**
     * Sets the remote active users
     */
    setRemoteActiveUsers: (users: IUserPresenceData[]) => void;

    /**
     * Sets the host document for local component
     */
    setHostDocumentForLocalComponent: (hostDocument?: IBaseDocumentData) => void;

    /**
     * Sets the base document data
     */
    setBaseDocumentData: (baseDocument?: IBaseDocumentData) => void;

    /**
     * Updates the document name and slug
     */
    updateDocumentName: (newDocumentName: string, newDocumentSlug: string) => void;

    /**
     * Updates the document description
     */
    updateDocumentDescription: (description: string) => void;
};

/**
 * The exported type of the store
 */
export type UseDocumentMetaStore = UseBoundStore<StoreApi<DocumentMetaStoreState>>;

/**
 * Creates the vanilla non-bound-to-React documentMeta store
 *
 * This is a vanilla Zustand store, and is only exported for use in tests. In components, you'd usually use the hooks
 * below, and even when trying to access low-level details of the store, you'd use the bound version of this store.
 */
export function createDocumentMetaStore(): UseDocumentMetaStore {
    return create<DocumentMetaStoreState>()(
        devtools(
            (set): DocumentMetaStoreState => {
                return {
                    remoteActiveUsers: [],

                    setDocumentOwner: (owner?: IUserData) => {
                        set({documentOwner: owner}, false, "setDocumentOwner");
                    },

                    setDocumentOrganizationOwner: (organizationOwner?: Organization) => {
                        set({documentOrganizationOwner: organizationOwner}, false, "setDocumentOrganizationOwner");
                    },

                    setDocumentEnterpriseOwner: (enterpriseOwner?: Enterprise) => {
                        set({documentEnterpriseOwner: enterpriseOwner}, false, "setDocumentEnterpriseOwner");
                    },

                    setDocumentEnterpriseOwnerDefaultOrganization: (
                        enterpriseOwnerDefaultOrganization?: EnterpriseOrganization,
                    ) => {
                        set(
                            {documentEnterpriseOwnerDefaultOrganization: enterpriseOwnerDefaultOrganization},
                            false,
                            "setDocumentEnterpriseOwnerDefaultOrganization",
                        );
                    },

                    setRemoteActiveUsers: (users: IUserPresenceData[]) => {
                        set({remoteActiveUsers: users}, false, "setRemoteActiveUsers");
                    },

                    setHostDocumentForLocalComponent: (hostDocument?: IBaseDocumentData) => {
                        set({hostDocumentForLocalComponent: hostDocument}, false, "setHostDocumentForLocalComponent");
                    },

                    setBaseDocumentData: (baseDocument?: IBaseDocumentData) => {
                        set({baseDocument}, false, "setBaseDocumentData");
                    },

                    updateDocumentName: (newDocumentName: string, newDocumentSlug: string) => {
                        set(
                            (state) => {
                                if (!state.baseDocument) return state;
                                return {
                                    baseDocument: {
                                        ...state.baseDocument,
                                        name: newDocumentName,
                                        slug: newDocumentSlug,
                                    },
                                };
                            },
                            false,
                            "updateDocumentName",
                        );
                    },

                    updateDocumentDescription: (description: string) => {
                        set(
                            (state) => {
                                if (!state.baseDocument) return state;
                                return {
                                    baseDocument: {
                                        ...state.baseDocument,
                                        description,
                                    },
                                };
                            },
                            false,
                            "updateDocumentDescription",
                        );
                    },
                };
            },
            {enabled: isDevEnv() && !areWeTestingWithJest(), name: "DocumentMetaStore"},
        ),
    );
}

/**
 * Hook to get the document owner
 */
export function useDocumentOwner(): IUserData | undefined {
    return useFluxServices().useDocumentMetaStore((state) => state.documentOwner);
}

/**
 * Hook to get the document organization owner
 */
export function useDocumentOrganizationOwner(): Organization | undefined {
    return useFluxServices().useDocumentMetaStore((state) => state.documentOrganizationOwner);
}

/**
 * Hook to get the document enterprise owner
 */
export function useDocumentEnterpriseOwner(): Enterprise | undefined {
    return useFluxServices().useDocumentMetaStore((state) => state.documentEnterpriseOwner);
}

/**
 * Hook to get the default organization for the enterprise owner
 */
export function useDocumentEnterpriseOwnerDefaultOrganization(): EnterpriseOrganization | undefined {
    return useFluxServices().useDocumentMetaStore((state) => state.documentEnterpriseOwnerDefaultOrganization);
}

/**
 * Hook to get the enterprise owner with default organization combined
 */
export function useDocumentEnterpriseOwnerWithDefaultOrganization(): EnterpriseWithDefaultOrganization | undefined {
    return useFluxServices().useDocumentMetaStore((state) => {
        const {documentEnterpriseOwner, documentEnterpriseOwnerDefaultOrganization} = state;
        if (!documentEnterpriseOwner || !documentEnterpriseOwnerDefaultOrganization) {
            return undefined;
        }
        return {
            ...documentEnterpriseOwner,
            defaultOrganization: documentEnterpriseOwnerDefaultOrganization,
        };
    });
}

/**
 * Hook to get the remote active users
 */
export function useRemoteActiveUsers(): IUserPresenceData[] {
    return useFluxServices().useDocumentMetaStore((state) => state.remoteActiveUsers);
}

/**
 * Hook to get the UIDs of remote active users
 * NOTE: Uses shallow comparison to avoid unnecessary re-renders
 */
export function useRemoteActiveUsersUids(): string[] {
    return useFluxServices().useDocumentMetaStore(
        useShallow((state) => state.remoteActiveUsers.map((user) => user.user_uid)),
    );
}

/**
 * Hook to get a specific remote active user by UID
 * NOTE: Uses shallow comparison to avoid unnecessary re-renders
 */
export function useRemoteActiveUser(userUid: string): IUserPresenceData | undefined {
    return useFluxServices().useDocumentMetaStore(
        useShallow((state) => state.remoteActiveUsers.find((user) => user.user_uid === userUid)),
    );
}

/**
 * Hook to get the base slug for local component
 */
export function useBaseSlugForLocalComponent(): Slug | undefined {
    return useFluxServices().useDocumentMetaStore((state) => state.hostDocumentForLocalComponent?.slug);
}

/**
 * Hook to get the base document
 */
export function useBaseDocument(): IBaseDocumentData | undefined {
    return useFluxServices().useDocumentMetaStore((state) => state.baseDocument);
}

/**
 * Hook to get the document UID
 */
export function useDocumentUid(): string | undefined {
    return useFluxServices().useDocumentMetaStore((state) => state.baseDocument?.uid);
}

/**
 * Hook to get the document name
 */
export function useDocumentName(): string | undefined {
    return useFluxServices().useDocumentMetaStore((state) => state.baseDocument?.name);
}

/**
 * Hook to get the document fork count
 */
export function useDocumentForkCount(): number | undefined {
    return useFluxServices().useDocumentMetaStore((state) => state.baseDocument?.fork_count);
}

/**
 * Hook to get the document star count
 */
export function useDocumentStarCount(): number | undefined {
    return useFluxServices().useDocumentMetaStore((state) => state.baseDocument?.star_count);
}

/**
 * Hook to get the document usage count
 */
export function useDocumentUsageCount(): number | undefined {
    return useFluxServices().useDocumentMetaStore((state) => state.baseDocument?.usage_count);
}

/**
 * Hook to get the canonical URL for the document
 */
export function useCanonicalUrl(): string | undefined {
    return useFluxServices().useDocumentMetaStore((state) => {
        const {baseDocument, hostDocumentForLocalComponent} = state;
        if (!baseDocument) {
            return undefined;
        }
        return getDocumentUrl(baseDocument, {
            baseSlugForLocalComponent: hostDocumentForLocalComponent?.slug,
        });
    });
}

/**
 * Hook to get the setDocumentOwner function
 */
export function useSetDocumentOwner(): (owner?: IUserData) => void {
    return useFluxServices().useDocumentMetaStore((state) => state.setDocumentOwner);
}

/**
 * Hook to get the setDocumentOrganizationOwner function
 */
export function useSetDocumentOrganizationOwner(): (organizationOwner?: Organization) => void {
    return useFluxServices().useDocumentMetaStore((state) => state.setDocumentOrganizationOwner);
}

/**
 * Hook to get the setDocumentEnterpriseOwner function
 */
export function useSetDocumentEnterpriseOwner(): (enterpriseOwner?: Enterprise) => void {
    return useFluxServices().useDocumentMetaStore((state) => state.setDocumentEnterpriseOwner);
}

/**
 * Hook to get the setDocumentEnterpriseOwnerDefaultOrganization function
 */
export function useSetDocumentEnterpriseOwnerDefaultOrganization(): (
    enterpriseOwnerDefaultOrganization?: EnterpriseOrganization,
) => void {
    return useFluxServices().useDocumentMetaStore((state) => state.setDocumentEnterpriseOwnerDefaultOrganization);
}

/**
 * Hook to get the setRemoteActiveUsers function
 */
export function useSetRemoteActiveUsers(): (users: IUserPresenceData[]) => void {
    return useFluxServices().useDocumentMetaStore((state) => state.setRemoteActiveUsers);
}

/**
 * Hook to get the setHostDocumentForLocalComponent function
 */
export function useSetHostDocumentForLocalComponent(): (hostDocument?: IBaseDocumentData) => void {
    return useFluxServices().useDocumentMetaStore((state) => state.setHostDocumentForLocalComponent);
}

/**
 * Hook to get the setBaseDocumentData function
 */
export function useSetBaseDocumentData(): (baseDocument?: IBaseDocumentData) => void {
    return useFluxServices().useDocumentMetaStore((state) => state.setBaseDocumentData);
}
